// Implements: F-DSG-002, F-UIX-020
/**
 * The live sample under "Tipografía" (the mockup v3 `.font-sample`): a heading and a specimen line in
 * the chosen font. Pure presentation.
 *
 * The stack reaches the sample through `style.setProperty` (the CSP has no `'unsafe-inline'` in
 * `style-src`); it comes from the code table, never from typed text. Loading the family's stylesheet
 * is the container's (`useGoogleFont`), so the sample falls back to the stack until it arrives.
 */

import { useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { isGoogleFont } from "../model/profileModel";
import { FONT_LABELS } from "./editorOptions";
import { FONT_STACK_CSS } from "./previewTokens";
import styles from "./ProfileEditor.module.css";

export interface FontSampleProps {
  /** The body font literal being edited. */
  readonly font: string;
}

export function FontSample({ font }: FontSampleProps): ReactElement {
  const { t } = useTranslation();
  const sample = useRef<HTMLDivElement>(null);
  const stack = FONT_STACK_CSS[font];
  useLayoutEffect(() => {
    const element = sample.current;
    if (element === null) return;
    if (stack === undefined) element.style.removeProperty("font-family");
    else element.style.setProperty("font-family", stack);
  }, [stack]);
  const key = (FONT_LABELS as Readonly<Record<string, (typeof FONT_LABELS)[keyof typeof FONT_LABELS]>>)[font];
  const family = key === undefined ? font : t(key);
  const source = isGoogleFont(font) ? t("design.font.group.google") : t("design.font.group.system");
  return (
    <div
      ref={sample}
      className={styles.fontSample}
      data-testid="design-font-sample"
      role="img"
      aria-label={`${t("design.sample.label")} ${family}`}
    >
      <b>{t("design.sample.title")}</b>
      <span>{`${family} · ${source} · ${t("design.sample.specimen")}`}</span>
    </div>
  );
}
